"use client";

import { type ListingsFilters } from "@/services/api";
import { useRouter } from "next/navigation";
import { useCallback } from "react";

interface UseURLSyncOptions {
  defaultFilters: ListingsFilters;
}

interface UseURLSyncReturn {
  updateURL: (filters: Partial<ListingsFilters>, page?: number) => void;
  getFiltersFromURL: (searchParams: URLSearchParams) => ListingsFilters;
  getPageFromURL: (searchParams: URLSearchParams) => number;
}

export function useURLSync({ defaultFilters }: UseURLSyncOptions): UseURLSyncReturn {
  const router = useRouter();

  const safeDecodeURIComponent = (str: string | null): string => {
    if (!str) return "";
    try {
      return decodeURIComponent(str);
    } catch {
      return str;
    }
  };

  const getFiltersFromURL = useCallback((searchParams: URLSearchParams): ListingsFilters => {
    const brands = searchParams.getAll("brands[]").map(safeDecodeURIComponent);
    const conditions = searchParams.getAll("conditions[]").map(safeDecodeURIComponent);
    const colors = searchParams.getAll("colors[]").map(safeDecodeURIComponent);
    const hardwares = searchParams.getAll("hardwares[]").map(safeDecodeURIComponent);
    const vendors = searchParams.getAll("vendors[]").map(safeDecodeURIComponent);
    const deals = searchParams.getAll("deals[]").map(safeDecodeURIComponent);

    return {
      sortOrder: safeDecodeURIComponent(searchParams.get("sortOrder")) || defaultFilters.sortOrder,
      minPrice: safeDecodeURIComponent(searchParams.get("minPrice")) || defaultFilters.minPrice,
      maxPrice: safeDecodeURIComponent(searchParams.get("maxPrice")) || defaultFilters.maxPrice,
      searchQuery: safeDecodeURIComponent(searchParams.get("searchQuery")) || defaultFilters.searchQuery || "",
      brands,
      conditions,
      colors,
      hardwares,
      vendors,
      deals,
    };
  }, [defaultFilters]);

  const getPageFromURL = useCallback((searchParams: URLSearchParams): number => {
    const page = safeDecodeURIComponent(searchParams.get("page"));
    return parseInt(page) || 1;
  }, []);

  const updateURL = useCallback((filters: Partial<ListingsFilters>, page: number = 1) => {
    const params = new URLSearchParams();

    if (page !== 1) {
      params.set("page", page.toString());
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item?.toString().trim()) {
            params.append(`${key}[]`, item);
          }
        });
      } else if (value != null && value !== "") {
        const stringValue = value.toString().trim();
        if (stringValue) {
          params.set(key, stringValue);
        }
      }
    });

    const urlString = params.toString();
    const newUrl = urlString ? `/listings?${urlString}` : "/listings";
    router.replace(newUrl);
  }, [router]);

  return {
    updateURL,
    getFiltersFromURL,
    getPageFromURL,
  };
}
